import { useEffect, useMemo, useState } from 'react';
import { BellRing, CalendarClock, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  useCreateDocumentSummaryConfig,
  useDeleteDocumentSummaryConfig,
  useDocumentSummaryConfigs,
  useReminderSettings,
  useUpdateDocumentSummaryConfig,
  useUpdateReminderSettings,
} from '@api/hooks/useReminders';
import { Card, CardContent, CardHeader, CardTitle } from '@components/common/Card';
import { Button } from '@components/common/Button';
import { Input } from '@components/common/Input';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { MultiSearchableSelect } from '@components/common/MultiSearchableSelect';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { InfoTooltip } from '@components/common/InfoTooltip';
import type {
  CreateDocumentSummaryConfigRequest,
  DocumentSummaryConfig,
  ReminderDateFieldOption,
  ReminderSettingsResponse,
  ReminderUserOption,
} from '@schemas/api';

interface DateRuleFormState {
  enabled: boolean;
  offsetInput: string;
  includeOverdue: boolean;
}

interface SummaryFormState {
  name: string;
  isActive: boolean;
  scopeType: 'tenant' | 'org_unit';
  scopeOrgUnitId: string;
  scheduleLocalTime: string;
  timezone: string;
  channels: string[];
  recipientTargets: string[];
  explicitUserIds: string[];
  dateRules: Record<string, DateRuleFormState>;
}

const DEFAULT_OFFSETS = '7, 3, 1, 0';
const DEFAULT_CHANNELS = ['in_app'];
const DEFAULT_APPROVAL_CHANNELS = ['in_app'];
const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'In-app inbox',
  email: 'Email',
  slack: 'Slack DM',
  teams: 'Teams DM',
  google_chat: 'Google Chat DM',
};

const serializeNumberList = (values: number[]) => values.join(', ');

const parseNumberList = (value: string, fieldName: string, allowZero = true): number[] => {
  if (!value.trim()) {
    return [];
  }

  const parsed = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const number = Number(item);
      if (!Number.isInteger(number)) {
        throw new Error(`${fieldName} must contain whole numbers`);
      }
      return number;
    });

  const cleaned = Array.from(new Set(parsed)).sort((a, b) => a - b);
  const invalid = cleaned.some((number) => allowZero ? number < 0 : number <= 0);
  if (invalid) {
    throw new Error(`${fieldName} must contain ${allowZero ? 'zero or positive' : 'positive'} values`);
  }

  return cleaned;
};

const ruleKey = (rule: Pick<ReminderDateFieldOption, 'entity_type' | 'date_field'>) => (
  `${rule.entity_type}:${rule.date_field}`
);

const buildDefaultDateRuleState = (availableDateFields: ReminderDateFieldOption[]) => {
  return availableDateFields.reduce<Record<string, DateRuleFormState>>((acc, option) => {
    acc[ruleKey(option)] = {
      enabled: option.date_field === 'due_date' || option.date_field === 'early_payment_discount_deadline',
      offsetInput: DEFAULT_OFFSETS,
      includeOverdue: false,
    };
    return acc;
  }, {});
};

const buildDefaultSummaryForm = (settings?: ReminderSettingsResponse): SummaryFormState => ({
  name: '',
  isActive: true,
  scopeType: 'tenant',
  scopeOrgUnitId: '',
  scheduleLocalTime: settings?.document_summary_defaults.default_time ?? '08:00',
  timezone: settings?.document_summary_defaults.timezone ?? 'UTC',
  channels: DEFAULT_CHANNELS,
  recipientTargets: ['tenant_admins'],
  explicitUserIds: [],
  dateRules: buildDefaultDateRuleState(settings?.available_date_fields ?? []),
});

const buildSummaryFormFromConfig = (
  config: DocumentSummaryConfig,
  availableDateFields: ReminderDateFieldOption[]
): SummaryFormState => {
  const defaultRules = buildDefaultDateRuleState(availableDateFields);

  for (const dateRule of config.date_rules) {
    const key = ruleKey(dateRule);
    defaultRules[key] = {
      enabled: true,
      offsetInput: serializeNumberList(dateRule.offset_days),
      includeOverdue: dateRule.include_overdue,
    };
  }

  return {
    name: config.name,
    isActive: config.is_active,
    scopeType: config.scope_type === 'org_unit' ? 'org_unit' : 'tenant',
    scopeOrgUnitId: config.scope_org_unit_id ?? '',
    scheduleLocalTime: config.schedule_local_time,
    timezone: config.timezone,
    channels: config.channels.length > 0 ? config.channels : DEFAULT_CHANNELS,
    recipientTargets: config.recipient_rules.map((rule) => rule.target_type),
    explicitUserIds: config.recipient_rules
      .filter((rule) => rule.target_type === 'explicit_users')
      .flatMap((rule) => rule.user_ids),
    dateRules: defaultRules,
  };
};

const formatUserLabel = (user: ReminderUserOption) => {
  const fullName = `${user.first_name} ${user.last_name}`.trim();
  return fullName || user.email;
};

export const ReminderSettings = () => {
  const { data: settings, isLoading: settingsLoading, error: settingsError } = useReminderSettings();
  const { data: configList, isLoading: configsLoading, error: configsError } = useDocumentSummaryConfigs(true);
  const updateSettings = useUpdateReminderSettings();
  const createConfig = useCreateDocumentSummaryConfig();
  const updateConfig = useUpdateDocumentSummaryConfig();
  const deleteConfig = useDeleteDocumentSummaryConfig();

  const [approvalEnabled, setApprovalEnabled] = useState(false);
  const [approvalIntervals, setApprovalIntervals] = useState('');
  const [approvalChannels, setApprovalChannels] = useState<string[]>(DEFAULT_APPROVAL_CHANNELS);
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalationAfterHours, setEscalationAfterHours] = useState('');
  const [escalationTargetType, setEscalationTargetType] = useState('tenant_admins');
  const [escalationTargetIds, setEscalationTargetIds] = useState<string[]>([]);
  const [documentSummariesEnabled, setDocumentSummariesEnabled] = useState(true);
  const [defaultSummaryTime, setDefaultSummaryTime] = useState('08:00');
  const [defaultSummaryTimezone, setDefaultSummaryTimezone] = useState('UTC');
  const [settingsValidationError, setSettingsValidationError] = useState<string | null>(null);

  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [formState, setFormState] = useState<SummaryFormState>(buildDefaultSummaryForm());
  const [configValidationError, setConfigValidationError] = useState<string | null>(null);

  const summaryConfigs = configList?.items ?? [];

  const userOptions = useMemo(
    () => (settings?.available_users ?? []).map((user) => ({
      id: user.id,
      label: formatUserLabel(user),
      description: user.email,
    })),
    [settings?.available_users]
  );

  const orgUnitMap = useMemo(
    () => new Map((settings?.available_org_units ?? []).map((unit) => [unit.id, unit])),
    [settings?.available_org_units]
  );

  const recipientTargetMap = useMemo(
    () => new Map((settings?.available_recipient_targets ?? []).map((target) => [target.target_type, target])),
    [settings?.available_recipient_targets]
  );

  useEffect(() => {
    if (!settings) {
      return;
    }

    setApprovalEnabled(settings.approval_reminders.enabled);
    setApprovalIntervals(serializeNumberList(settings.approval_reminders.interval_hours));
    setApprovalChannels(
      settings.approval_reminders.channels.length > 0
        ? settings.approval_reminders.channels
        : DEFAULT_APPROVAL_CHANNELS
    );
    setEscalationEnabled(settings.approval_reminders.escalation_enabled);
    setEscalationAfterHours(
      settings.approval_reminders.escalation_after_hours != null
        ? String(settings.approval_reminders.escalation_after_hours)
        : ''
    );
    setEscalationTargetType(settings.approval_reminders.escalation_target_type ?? 'tenant_admins');
    setEscalationTargetIds(settings.approval_reminders.escalation_target_ids ?? []);
    setDocumentSummariesEnabled(settings.document_summary_defaults.enabled);
    setDefaultSummaryTime(settings.document_summary_defaults.default_time);
    setDefaultSummaryTimezone(settings.document_summary_defaults.timezone);
    setFormState((current) => {
      if (editingConfigId) {
        return current;
      }
      return buildDefaultSummaryForm(settings);
    });
  }, [editingConfigId, settings]);

  const resetSummaryForm = () => {
    setEditingConfigId(null);
    setConfigValidationError(null);
    setFormState(buildDefaultSummaryForm(settings));
  };

  const toggleChannel = (
    currentChannels: string[],
    channel: string,
    onChange: (channels: string[]) => void
  ) => {
    if (currentChannels.includes(channel)) {
      onChange(currentChannels.filter((item) => item !== channel));
      return;
    }
    onChange([...currentChannels, channel]);
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsValidationError(null);

      const intervalHours = approvalEnabled
        ? parseNumberList(approvalIntervals, 'Approval reminder intervals', false)
        : [];

      const payload = {
        approval_reminders: {
          enabled: approvalEnabled,
          interval_hours: intervalHours,
          channels: approvalChannels.length > 0 ? approvalChannels : DEFAULT_APPROVAL_CHANNELS,
          escalation_enabled: escalationEnabled,
          escalation_after_hours: escalationEnabled && escalationAfterHours.trim()
            ? Number(escalationAfterHours)
            : null,
          escalation_target_type: escalationEnabled ? escalationTargetType || null : null,
          escalation_target_ids: escalationEnabled && escalationTargetType === 'explicit_users'
            ? escalationTargetIds
            : [],
        },
        document_summary_defaults: {
          enabled: documentSummariesEnabled,
          default_time: defaultSummaryTime,
          timezone: defaultSummaryTimezone,
        },
      };

      if (escalationEnabled && payload.approval_reminders.escalation_after_hours != null) {
        if (!Number.isInteger(payload.approval_reminders.escalation_after_hours) || payload.approval_reminders.escalation_after_hours <= 0) {
          throw new Error('Escalation delay must be a positive whole number of hours');
        }
      }

      if (escalationEnabled && escalationTargetType === 'explicit_users' && escalationTargetIds.length === 0) {
        throw new Error('Select at least one escalation recipient when using specific users');
      }

      await updateSettings.mutateAsync(payload);
    } catch (error) {
      setSettingsValidationError(error instanceof Error ? error.message : 'Failed to validate reminder settings');
    }
  };

  const handleEditConfig = (config: DocumentSummaryConfig) => {
    setEditingConfigId(config.id);
    setConfigValidationError(null);
    setFormState(buildSummaryFormFromConfig(config, settings?.available_date_fields ?? []));
  };

  const handleSaveSummaryConfig = async () => {
    try {
      setConfigValidationError(null);

      if (!formState.name.trim()) {
        throw new Error('Summary name is required');
      }

      if (formState.scopeType === 'org_unit' && !formState.scopeOrgUnitId) {
        throw new Error('Select an org unit for org-unit scoped summaries');
      }

      const enabledRules = (settings?.available_date_fields ?? [])
        .map((option) => ({ option, state: formState.dateRules[ruleKey(option)] }))
        .filter(({ state }) => state?.enabled);

      if (enabledRules.length === 0) {
        throw new Error('Select at least one document date rule');
      }

      const dateRules = enabledRules.map(({ option, state }) => {
        const offsetDays = parseNumberList(state.offsetInput, `${option.label} offsets`);
        if (offsetDays.length === 0 && !state.includeOverdue) {
          throw new Error(`${option.label} needs at least one offset or overdue enabled`);
        }

        return {
          entity_type: option.entity_type,
          date_field: option.date_field,
          offset_days: offsetDays,
          include_overdue: state.includeOverdue,
        };
      });

      const recipientRules = formState.recipientTargets.flatMap((targetType) => {
        if (targetType === 'org_unit_manager' && formState.scopeType !== 'org_unit') {
          return [];
        }
        if (targetType === 'explicit_users') {
          if (formState.explicitUserIds.length === 0) {
            throw new Error('Select at least one user for specific-user recipients');
          }
          return [{ target_type: targetType, user_ids: formState.explicitUserIds }];
        }
        return [{ target_type: targetType, user_ids: [] }];
      });

      if (recipientRules.length === 0) {
        throw new Error('Select at least one recipient target');
      }

      const payload: CreateDocumentSummaryConfigRequest = {
        name: formState.name.trim(),
        is_active: formState.isActive,
        scope_type: formState.scopeType,
        scope_org_unit_id: formState.scopeType === 'org_unit' ? formState.scopeOrgUnitId : null,
        date_rules: dateRules,
        channels: formState.channels.length > 0 ? formState.channels : DEFAULT_CHANNELS,
        recipient_rules: recipientRules,
        schedule_local_time: formState.scheduleLocalTime,
        timezone: formState.timezone,
      };

      if (editingConfigId) {
        await updateConfig.mutateAsync({
          configId: editingConfigId,
          data: payload,
        });
      } else {
        await createConfig.mutateAsync(payload);
      }

      resetSummaryForm();
    } catch (error) {
      setConfigValidationError(error instanceof Error ? error.message : 'Failed to validate summary config');
    }
  };

  const handleDeleteConfig = async (config: DocumentSummaryConfig) => {
    if (!window.confirm(`Delete summary config \"${config.name}\"?`)) {
      return;
    }

    await deleteConfig.mutateAsync(config.id);
    if (editingConfigId === config.id) {
      resetSummaryForm();
    }
  };

  if (settingsLoading || configsLoading) {
    return <LoadingSpinner />;
  }

  if (settingsError || configsError || !settings) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
        Failed to load reminder settings. Refresh and try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <BellRing className="mt-1 h-5 w-5 text-primary-500" />
            <div>
              <div className="flex items-center gap-2">
                <CardTitle>Approval Reminders</CardTitle>
                <InfoTooltip>Approvers are notified when a step opens and at any intervals you set.</InfoTooltip>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 text-sm font-medium text-content-primary">
            <input
              type="checkbox"
              checked={approvalEnabled}
              onChange={(event) => setApprovalEnabled(event.target.checked)}
            />
            Enable approval-step reminders
          </label>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Reminder intervals (hours)"
              value={approvalIntervals}
              onChange={(event) => setApprovalIntervals(event.target.value)}
              placeholder="24, 48, 72"
              helperText="Optional comma-separated positive whole hours."
              disabled={!approvalEnabled}
            />
            <div>
              <p className="mb-1.5 text-sm font-medium text-content-secondary">Channels</p>
              <div className="flex flex-wrap gap-3 rounded-lg border border-border-default p-3">
                {['in_app', 'email', 'slack', 'teams', 'google_chat'].map((channel) => (
                  <label key={channel} className="flex items-center gap-2 text-sm text-content-primary">
                    <input
                      type="checkbox"
                      checked={approvalChannels.includes(channel)}
                      onChange={() => toggleChannel(approvalChannels, channel, setApprovalChannels)}
                      disabled={!approvalEnabled}
                    />
                    {CHANNEL_LABELS[channel] ?? channel}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border-default p-4 space-y-4">
            <label className="flex items-center gap-3 text-sm font-medium text-content-primary">
              <input
                type="checkbox"
                checked={escalationEnabled}
                onChange={(event) => setEscalationEnabled(event.target.checked)}
                disabled={!approvalEnabled}
              />
              Enable escalation
            </label>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="Escalate after (hours)"
                value={escalationAfterHours}
                onChange={(event) => setEscalationAfterHours(event.target.value)}
                placeholder="72"
                disabled={!approvalEnabled || !escalationEnabled}
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-content-secondary">
                  Escalation recipients
                </label>
                <SearchableSelect
                  value={escalationTargetType}
                  onChange={setEscalationTargetType}
                  disabled={!approvalEnabled || !escalationEnabled}
                  options={[
                    { id: 'tenant_admins', label: 'Tenant admins' },
                    { id: 'explicit_users', label: 'Specific users' },
                  ]}
                  placeholder="Escalation recipients"
                  clearable={false}
                />
              </div>
            </div>

            {escalationEnabled && escalationTargetType === 'explicit_users' && (
              <MultiSearchableSelect
                options={userOptions}
                value={escalationTargetIds}
                onChange={setEscalationTargetIds}
                placeholder="Select escalation recipients"
              />
            )}
          </div>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-1 h-5 w-5 text-primary-500" />
            <div>
              <div className="flex items-center gap-2">
                <CardTitle>Document Summary Defaults</CardTitle>
                <InfoTooltip>These defaults prefill tenant-wide and org-unit deadline summaries.</InfoTooltip>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 text-sm font-medium text-content-primary">
            <input
              type="checkbox"
              checked={documentSummariesEnabled}
              onChange={(event) => setDocumentSummariesEnabled(event.target.checked)}
            />
            Enable scheduled document summaries
          </label>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Default local delivery time"
              type="time"
              step={300}
              value={defaultSummaryTime}
              onChange={(event) => setDefaultSummaryTime(event.target.value)}
              helperText="Use 5-minute increments to match the backend summary sweep."
            />
            <Input
              label="Default timezone"
              value={defaultSummaryTimezone}
              onChange={(event) => setDefaultSummaryTimezone(event.target.value)}
              placeholder="Europe/Stockholm"
              helperText="IANA timezone used to evaluate local delivery times."
            />
          </div>

          {settingsValidationError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
              {settingsValidationError}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} loading={updateSettings.isPending}>
              Save Reminder Defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <CardTitle>{editingConfigId ? 'Edit Summary Config' : 'New Summary Config'}</CardTitle>
                <InfoTooltip>Set the scope, recipients, date windows, and delivery schedule.</InfoTooltip>
              </div>
            </div>
            {editingConfigId && (
              <Button variant="secondary" onClick={resetSummaryForm}>
                Cancel Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Summary name"
              value={formState.name}
              onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
              placeholder="Tenant AP deadlines"
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-content-secondary">Scope</label>
              <SearchableSelect
                value={formState.scopeType}
                onChange={(value) => {
                  const nextScope = value as 'tenant' | 'org_unit';
                  setFormState((current) => ({
                    ...current,
                    scopeType: nextScope,
                    scopeOrgUnitId: nextScope === 'org_unit' ? current.scopeOrgUnitId : '',
                    recipientTargets: nextScope === 'org_unit'
                      ? current.recipientTargets
                      : current.recipientTargets.filter((target) => target !== 'org_unit_manager'),
                  }));
                }}
                options={[
                  { id: 'tenant', label: 'Tenant' },
                  { id: 'org_unit', label: 'Org unit' },
                ]}
                placeholder="Scope"
                clearable={false}
              />
            </div>
          </div>

          {formState.scopeType === 'org_unit' && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-content-secondary">Org unit</label>
              <SearchableSelect
                value={formState.scopeOrgUnitId}
                onChange={(value) => setFormState((current) => ({ ...current, scopeOrgUnitId: value }))}
                options={[
                  { id: '', label: 'Select an org unit' },
                  ...(settings.available_org_units ?? []).map((unit) => ({ id: unit.id, label: unit.name })),
                ]}
                placeholder="Select an org unit"
                clearable={false}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Input
              label="Delivery time"
              type="time"
              step={300}
              value={formState.scheduleLocalTime}
              onChange={(event) => setFormState((current) => ({ ...current, scheduleLocalTime: event.target.value }))}
            />
            <Input
              label="Timezone"
              value={formState.timezone}
              onChange={(event) => setFormState((current) => ({ ...current, timezone: event.target.value }))}
              placeholder="UTC"
            />
            <div className="flex items-end">
              <label className="flex items-center gap-3 text-sm font-medium text-content-primary">
                <input
                  type="checkbox"
                  checked={formState.isActive}
                  onChange={(event) => setFormState((current) => ({ ...current, isActive: event.target.checked }))}
                />
                Active
              </label>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-content-secondary">Channels</p>
            <div className="flex flex-wrap gap-3 rounded-lg border border-border-default p-3">
              {['in_app', 'email'].map((channel) => (
                <label key={channel} className="flex items-center gap-2 text-sm text-content-primary">
                  <input
                    type="checkbox"
                    checked={formState.channels.includes(channel)}
                    onChange={() => toggleChannel(formState.channels, channel, (channels) => (
                      setFormState((current) => ({ ...current, channels }))
                    ))}
                  />
                  {CHANNEL_LABELS[channel] ?? channel}
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-content-secondary">Recipients</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {(settings.available_recipient_targets ?? []).map((target) => {
                const disabled = target.target_type === 'org_unit_manager' && formState.scopeType !== 'org_unit';
                return (
                  <label
                    key={target.target_type}
                    className={`rounded-lg border p-3 text-sm ${disabled ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={formState.recipientTargets.includes(target.target_type)}
                        disabled={disabled}
                        onChange={() => {
                          setFormState((current) => ({
                            ...current,
                            recipientTargets: current.recipientTargets.includes(target.target_type)
                              ? current.recipientTargets.filter((item) => item !== target.target_type)
                              : [...current.recipientTargets, target.target_type],
                          }));
                        }}
                      />
                      <div>
                        <p className="font-medium text-content-primary">{target.label}</p>
                        <p className="text-content-muted">
                          {target.requires_org_unit_scope
                            ? 'Available only for org-unit scoped summaries.'
                            : target.requires_user_selection
                            ? 'Send this summary to selected users.'
                            : 'Include this audience in every delivery.'}
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {formState.recipientTargets.includes('explicit_users') && (
            <div>
              <p className="mb-2 text-sm font-medium text-content-secondary">Specific users</p>
              <MultiSearchableSelect
                options={userOptions}
                value={formState.explicitUserIds}
                onChange={(explicitUserIds) => setFormState((current) => ({ ...current, explicitUserIds }))}
                placeholder="Search users to receive this summary"
              />
            </div>
          )}

          <div className="space-y-3">
            <p className="text-sm font-medium text-content-secondary">Date rules</p>
            {(settings.available_date_fields ?? []).map((option) => {
              const key = ruleKey(option);
              const state = formState.dateRules[key] ?? {
                enabled: false,
                offsetInput: '',
                includeOverdue: false,
              };
              return (
                <div key={key} className="rounded-lg border border-border-default p-4 space-y-4">
                  <label className="flex items-center gap-3 text-sm font-medium text-content-primary">
                    <input
                      type="checkbox"
                      checked={state.enabled}
                      onChange={(event) => setFormState((current) => ({
                        ...current,
                        dateRules: {
                          ...current.dateRules,
                          [key]: {
                            ...state,
                            enabled: event.target.checked,
                          },
                        },
                      }))}
                    />
                    {option.label}
                  </label>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="Offsets (days)"
                      value={state.offsetInput}
                      onChange={(event) => setFormState((current) => ({
                        ...current,
                        dateRules: {
                          ...current.dateRules,
                          [key]: {
                            ...state,
                            offsetInput: event.target.value,
                          },
                        },
                      }))}
                      placeholder="7, 3, 1, 0"
                      disabled={!state.enabled}
                    />
                    <div className="flex items-end">
                      <label className="flex items-center gap-3 text-sm font-medium text-content-primary">
                        <input
                          type="checkbox"
                          checked={state.includeOverdue}
                          onChange={(event) => setFormState((current) => ({
                            ...current,
                            dateRules: {
                              ...current.dateRules,
                              [key]: {
                                ...state,
                                includeOverdue: event.target.checked,
                              },
                            },
                          }))}
                          disabled={!state.enabled}
                        />
                        Include overdue documents
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {configValidationError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
              {configValidationError}
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            {editingConfigId ? (
              <Button variant="secondary" onClick={resetSummaryForm}>
                Cancel
              </Button>
            ) : null}
            <Button
              onClick={handleSaveSummaryConfig}
              loading={createConfig.isPending || updateConfig.isPending}
              className="inline-flex items-center gap-2"
            >
              {editingConfigId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editingConfigId ? 'Update Summary Config' : 'Create Summary Config'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>Configured Summaries</CardTitle>
            <InfoTooltip>Tenant-wide and org-unit deadline summary definitions, whether active or inactive.</InfoTooltip>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {summaryConfigs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border-default p-6 text-sm text-content-secondary">
              No summary configs yet.
            </div>
          ) : summaryConfigs.map((config) => {
            const scopeLabel = config.scope_type === 'org_unit'
              ? orgUnitMap.get(config.scope_org_unit_id ?? '')?.name ?? 'Org unit'
              : 'Tenant-wide';
            const recipientSummary = config.recipient_rules.map((rule) => {
              if (rule.target_type === 'explicit_users') {
                const names = rule.user_ids
                  .map((userId) => settings.available_users.find((user) => user.id === userId))
                  .filter(Boolean)
                  .map((user) => formatUserLabel(user as ReminderUserOption));
                return names.length > 0 ? `Users: ${names.join(', ')}` : 'Users';
              }
              return recipientTargetMap.get(rule.target_type)?.label ?? rule.target_type;
            }).join(' • ');
            const ruleSummary = config.date_rules.map((rule) => {
              const option = settings.available_date_fields.find((field) => (
                field.entity_type === rule.entity_type && field.date_field === rule.date_field
              ));
              const offsets = rule.offset_days.length > 0 ? rule.offset_days.join(', ') : 'none';
              return `${option?.label ?? rule.date_field} (${offsets}${rule.include_overdue ? ', overdue' : ''})`;
            }).join(' • ');

            return (
              <div key={config.id} className="rounded-lg border border-border-default p-4 space-y-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-content-primary">{config.name}</h3>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-content-secondary">
                      {scopeLabel} • {config.schedule_local_time} {config.timezone}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => handleEditConfig(config)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteConfig(config)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-content-secondary">
                  <span className="font-medium text-content-primary">Recipients:</span> {recipientSummary}
                </p>
                <p className="text-sm text-content-secondary">
                  <span className="font-medium text-content-primary">Rules:</span> {ruleSummary}
                </p>
                <p className="text-sm text-content-secondary">
                  <span className="font-medium text-content-primary">Channels:</span> {config.channels.join(', ')}
                </p>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};
